import React, { useContext, useState } from "react";
import { UserContext, UserProvider } from "../contexts/UserContext";
import NavLink from "../components/NavLink";
import NestedSubMenu from "../components/NestedSubMenu";
import { URL } from "../utils";
import { useLocation } from "react-router-dom";
import ConditionalRender from "../components/ConditionalRender";
import RoleBadge from "../components/RoleBadge";
import CountryBadge from "../components/CountryBadge";
import { usePermissions } from "../hooks/usePermissions";
import { ROLES } from "../constants";

// Composant pour un élément de menu avec sous-menu
function MenuItemWithSubmenu({
  title,
  icon,
  children,
  isOpen,
  onToggle,
  isActive,
}) {
  return (
    <li className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center justify-between w-full p-3 text-left rounded-lg hover:bg-gray-100 group transition-all duration-200 ${
          isActive
            ? "bg-orange-50 text-orange-500 font-medium"
            : isOpen
            ? "bg-gray-50 text-gray-700 font-medium"
            : "text-gray-700"
        }`}
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-3">
          <span
            className={`transition-colors duration-200 flex-shrink-0 ${
              isActive
                ? "text-orange-500"
                : "text-gray-500 group-hover:text-gray-700"
            }`}
          >
            {icon}
          </span>
          <span className="text-left truncate">{title}</span>
        </div>
        <svg
          className={`w-4 h-4 transition-all duration-200 ${
            isActive ? "text-orange-500" : "text-gray-400"
          } ${isOpen ? "rotate-180" : ""}`}
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {isOpen && (
        <ul className="py-2 ml-6 space-y-1 border-l-2 border-gray-100 pl-3">
          {children}
        </ul>
      )}
    </li>
  );
}

// Composant pour un sous-élément de menu
function SubMenuItem({ to, icon, children }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          `flex items-center w-full p-2.5 text-sm rounded-lg transition-colors duration-150 ${
            isActive
              ? "bg-orange-50 text-orange-600 font-medium"
              : "text-gray-600 hover:bg-gray-100"
          }`
        }
      >
        <span className={`mr-3 transition-colors duration-150 flex-shrink-0`}>
          {icon}
        </span>
        <span className="truncate">{children}</span>
      </NavLink>
    </li>
  );
}

function SidebarMenu({ user }) {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    stock: false,
    clients: false,
    particuliers: false,
    professionnels: false,
    commerciaux: false,
    users: false,
    ventes: false,
    prospects: false,
    autres: false,
  });

  const isPathActive = (paths) => {
    return paths.some((path) => location.pathname.startsWith(path));
  };

  const isStockActive = isPathActive(["/stock"]) || location.pathname === "/";
  const isClientsActive = isPathActive(["/clients"]);
  const isParticuliersActive = isPathActive(["/clients/particuliers"]);
  const isProfessionnelsActive = isPathActive(["/clients/professionnels"]);
  const isCommerciauxActive = isPathActive(["/commercials"]);
  const isUsersActive = isPathActive(["/users"]);
  const isVentesActive = isPathActive(["/sales"]);
  const isProspectsActive = isPathActive(["/prospects"]);
  const isAutresActive = isPathActive(["/autres-sorties", "/autres-entrees"]);

  const { hasPermission } = usePermissions();

  React.useEffect(() => {
    setOpenMenus((prev) => ({
      ...prev,
      stock: isStockActive || prev.stock,
      clients:
        isClientsActive ||
        isParticuliersActive ||
        isProfessionnelsActive ||
        prev.clients,
      particuliers: isParticuliersActive || prev.particuliers,
      professionnels: isProfessionnelsActive || prev.professionnels,
      commerciaux: isCommerciauxActive || prev.commerciaux,
      users: isUsersActive || prev.users,
      ventes: isVentesActive || prev.ventes,
      prospects: isProspectsActive || prev.prospects,
      autres: isAutresActive || prev.autres,
    }));
  }, [
    isStockActive,
    isClientsActive,
    isParticuliersActive,
    isProfessionnelsActive,
    isCommerciauxActive,
    isUsersActive,
    isVentesActive,
    isProspectsActive,
    isAutresActive,
  ]);

  const toggleMenu = (menuKey) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  return (
    <ul className="space-y-1 p-3">
      {/* Dashboard */}
      <ConditionalRender
        requiredPermissions={["canViewDashboard", "canViewAllStores"]}
      >
        <NavLink
          to={
            hasPermission("canViewAllStores") &&
            !(user.role === ROLES.RESP_BOUTIQUE)
              ? "/"
              : `/stock/sell-point-details/${user.magasin}`
          }
          className={({ isActive }) =>
            `flex items-center p-3 rounded-lg transition-colors duration-200 ${
              isActive
                ? "bg-orange-50 text-orange-600 font-medium"
                : "text-gray-700 hover:bg-gray-100"
            }`
          }
        >
          <svg
            className={`w-5 h-5 mr-3 flex-shrink-0 transition-colors duration-200`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
          </svg>
          <span>Stores</span>
        </NavLink>
      </ConditionalRender>

      {hasPermission("canViewAllWarehouses") && (
        <ConditionalRender
          requiredPermissions={["canViewAllWarehouses", "canViewAllStock"]}
        >
          <li>
            <NavLink
              to="/stock"
              className={({ isActive }) =>
                `flex items-center p-3 rounded-lg transition-colors duration-200 ${
                  isActive
                    ? "bg-orange-50 text-orange-600 font-medium"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <svg
                className="w-5 h-5 mr-3 flex-shrink-0 transition-colors duration-200"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 7.205c4.418 0 8-1.165 8-2.602C20 3.165 16.418 2 12 2S4 3.165 4 4.603c0 1.437 3.582 2.602 8 2.602ZM12 22c4.963 0 8-1.686 8-2.603v-4.404c-.052.032-.112.06-.165.09a7.75 7.75 0 0 1-.745.387c-.193.088-.394.173-.6.253-.063.024-.124.05-.189.073a18.934 18.934 0 0 1-6.3.998c-2.135.027-4.26-.31-6.3-.998-.065-.024-.126-.05-.189-.073a10.143 10.143 0 0 1-.852-.373 7.75 7.75 0 0 1-.493-.267c-.053-.03-.113-.058-.165-.09v4.404C4 20.315 7.037 22 12 22Zm7.09-13.928a9.91 9.91 0 0 1-.6.253c-.063.025-.124.05-.189.074a18.935 18.935 0 0 1-6.3.998c-2.135.027-4.26-.31-6.3-.998-.065-.024-.126-.05-.189-.074a10.163 10.163 0 0 1-.852-.372 7.816 7.816 0 0 1-.493-.268c-.055-.03-.115-.058-.167-.09V12c0 .917 3.037 2.603 8 2.603s8-1.686 8-2.603V7.596c-.052.031-.112.059-.165.09a7.816 7.816 0 0 1-.745.386Z" />
              </svg>

              <span>Entrepôts</span>
            </NavLink>
          </li>
        </ConditionalRender>
      )}

      {/* Clients & Prospects */}
      <ConditionalRender
        requiredPermissions={["canViewAllClients", "canViewAssignedClients"]}
      >
        <MenuItemWithSubmenu
          title="Clients"
          isOpen={openMenus.clients}
          onToggle={() => toggleMenu("clients")}
          isActive={isClientsActive}
          icon={
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                d="M12 6a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm-1.5 8a4 4 0 0 0-4 4 2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 4 4 0 0 0-4-4h-3Zm6.82-3.096a5.51 5.51 0 0 0-2.797-6.293 3.5 3.5 0 1 1 2.796 6.292ZM19.5 18h.5a2 2 0 0 0 2-2 4 4 0 0 0-4-4h-1.1a5.503 5.503 0 0 1-.471.762A5.998 5.998 0 0 1 19.5 18ZM4 7.5a3.5 3.5 0 0 1 5.477-2.889 5.5 5.5 0 0 0-2.796 6.293A3.501 3.501 0 0 1 4 7.5ZM7.1 12H6a4 4 0 0 0-4 4 2 2 0 0 0 2 2h.5a5.998 5.998 0 0 1 3.071-5.238A5.505 5.505 0 0 1 7.1 12Z"
                clipRule="evenodd"
              />
            </svg>
          }
        >
          <SubMenuItem
            to="/clients"
            icon={
              <svg
                className="w-4 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11 9h6m-6 3h6m-6 3h6M6.996 9h.01m-.01 3h.01m-.01 3h.01M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
                />
              </svg>
            }
          >
            Tous les Clients
          </SubMenuItem>

          {/* Sous-sous-menu Particuliers */}
          <NestedSubMenu
            title="Particuliers"
            isActive={isParticuliersActive}
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            }
          >
            <ConditionalRender
              requiredPermissions={[
                "canViewAllClients",
                "canViewAssignedClients",
              ]}
            >
              <SubMenuItem
                to="/clients/particuliers"
                icon={
                  <svg
                    className="w-4 h-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-3 5h3m-6 0h.01M12 16h3m-6 0h.01M10 3v4h4V3h-4Z"
                    />
                  </svg>
                }
              >
                Liste des particuliers
              </SubMenuItem>
            </ConditionalRender>
            <ConditionalRender requiredPermissions="canCreateClient">
              <SubMenuItem
                to="/clients/particuliers/add"
                icon={
                  <svg
                    className="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                }
              >
                Ajouter un particulier
              </SubMenuItem>
            </ConditionalRender>
          </NestedSubMenu>

          {/* Sous-sous-menu Professionnels */}
          <NestedSubMenu
            title="Professionnels"
            isActive={isProfessionnelsActive}
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10z" />
              </svg>
            }
          >
            <ConditionalRender
              requiredPermissions={[
                "canViewAllClients",
                "canViewAssignedClients",
              ]}
            >
              <SubMenuItem
                to="/clients/professionnels"
                icon={
                  <svg
                    className="w-4 h-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-3 5h3m-6 0h.01M12 16h3m-6 0h.01M10 3v4h4V3h-4Z"
                    />
                  </svg>
                }
              >
                Liste des professionnels
              </SubMenuItem>
            </ConditionalRender>
            <ConditionalRender requiredPermissions="canCreateClient">
              <SubMenuItem
                to="/clients/professionnels/add"
                icon={
                  <svg
                    className="w-3.5 h-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                }
              >
                Ajouter un professionnel
              </SubMenuItem>
            </ConditionalRender>
          </NestedSubMenu>
        </MenuItemWithSubmenu>
      </ConditionalRender>

      <ConditionalRender
        requiredPermissions={[
          "canViewAllProspects",
          "canViewAssignedProspects",
          "canCreateProspect",
        ]}
      >
        <MenuItemWithSubmenu
          title="Prospects"
          isOpen={openMenus.prospects}
          onToggle={() => toggleMenu("prospects")}
          isActive={isProspectsActive}
          icon={
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 2c-1.10457 0-2 .89543-2 2v4c0 .55228.44772 1 1 1s1-.44772 1-1V4h12v7h-2c-.5523 0-1 .4477-1 1v2h-1c-.5523 0-1 .4477-1 1s.4477 1 1 1h5c.5523 0 1-.4477 1-1V3.85714C20 2.98529 19.3667 2 18.268 2H6Z" />
              <path d="M6 11.5C6 9.567 7.567 8 9.5 8S13 9.567 13 11.5 11.433 15 9.5 15 6 13.433 6 11.5ZM4 20c0-2.2091 1.79086-4 4-4h3c2.2091 0 4 1.7909 4 4 0 1.1046-.8954 2-2 2H6c-1.10457 0-2-.8954-2-2Z" />
            </svg>
          }
        >
          <ConditionalRender
            requiredPermissions={[
              "canViewAllProspects",
              "canViewAssignedProspects",
            ]}
          >
            <SubMenuItem
              to="/prospects"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A3.01 3.01 0 0 0 17.06 7H16.5c-1.1 0-2 .9-2 2v11h2zm-7.5-10.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10.5s.67 1.5 1.5 1.5zm1.5 1h-3c-1.1 0-2 .9-2 2v8h2v-3h3v3h2v-8c0-1.1-.9-2-2-2z" />
                </svg>
              }
            >
              Prospects
            </SubMenuItem>
          </ConditionalRender>
          <ConditionalRender requiredPermissions={"canCreateProspect"}>
            <SubMenuItem
              to="/prospects/add"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              }
            >
              Ajouter un prospect
            </SubMenuItem>
          </ConditionalRender>
        </MenuItemWithSubmenu>
      </ConditionalRender>

      {/* Commerciaux */}
      <ConditionalRender
        requiredPermissions={["canViewAllCommercials", "canCreateCommercial"]}
      >
        <MenuItemWithSubmenu
          title="Commerciaux"
          isOpen={openMenus.commerciaux}
          onToggle={() => toggleMenu("commerciaux")}
          isActive={isCommerciauxActive}
          icon={
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          }
        >
          <ConditionalRender requiredPermissions="canViewAllCommercials">
            <SubMenuItem
              to="/commercials"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              }
            >
              Liste des commerciaux
            </SubMenuItem>
          </ConditionalRender>
          <ConditionalRender requiredPermissions="canCreateCommercial">
            <SubMenuItem
              to="/commercials/add"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              }
            >
              Ajouter un commercial
            </SubMenuItem>
          </ConditionalRender>
        </MenuItemWithSubmenu>
      </ConditionalRender>

      {/* Utilisateurs */}
      <ConditionalRender requiredPermissions="canManageUsers">
        <MenuItemWithSubmenu
          title="Utilisateurs"
          isOpen={openMenus.users}
          onToggle={() => toggleMenu("users")}
          isActive={isUsersActive}
          icon={
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 7c0-2.21-1.79-4-4-4s-4 1.79-4 4 1.79 4 4 4 4-1.79 4-4zM12 14c-3.31 0-10 1.66-10 5v3h20v-3c0-3.34-6.69-5-10-5z" />
            </svg>
          }
        >
          <SubMenuItem
            to="/users"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4h2v-7.5c0-.83.67-1.5 1.5-1.5S12 9.67 12 10.5V18h2v-4h3v4h2V8.5c0-1.11-.89-2-2-2h-3c-.55 0-1.05.22-1.41.59L9.59 10.5c-.37.37-.59.87-.59 1.41V18H4z" />
              </svg>
            }
          >
            Liste des utilisateurs
          </SubMenuItem>
          <SubMenuItem
            to="/users/add"
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V8h4v2H6zm0 4V12h4v2H6zm0 4V16h4v2H6zm6 0h6v-2h-6v2zm0-4h6v-2h-6v2zm0-4h6V8h-6v2zm8 8v-2h2v2h-2zm0-4v-2h2v2h-2z" />
              </svg>
            }
          >
            Ajouter un utilisateur
          </SubMenuItem>
        </MenuItemWithSubmenu>
      </ConditionalRender>

      {/* Ventes & Facturation */}
      <ConditionalRender
        requiredPermissions={[
          "canViewAllSales",
          "canViewAssignedSales",
          "canCreateSale",
        ]}
      >
        <MenuItemWithSubmenu
          title="Ventes & Facturation"
          isOpen={openMenus.ventes}
          onToggle={() => toggleMenu("ventes")}
          isActive={isVentesActive}
          icon={
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
        >
          <ConditionalRender
            requiredPermissions={["canViewAllSales", "canViewAssignedSales"]}
          >
            <SubMenuItem
              to="/sales"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
                </svg>
              }
            >
              Toutes les factures
            </SubMenuItem>
          </ConditionalRender>
          <ConditionalRender requiredPermissions="canCreateSale">
            <SubMenuItem
              to="/sales/add"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              }
            >
              Nouvelle facture
            </SubMenuItem>
          </ConditionalRender>
        </MenuItemWithSubmenu>
      </ConditionalRender>

      {/* Autres Opérations */}
      <ConditionalRender
        requiredPermissions={["canCreateStockEntry", "canCreateStockOutflow"]}
      >
        <MenuItemWithSubmenu
          title="Autres Opérations"
          isOpen={openMenus.autres}
          onToggle={() => toggleMenu("autres")}
          isActive={isAutresActive}
          icon={
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          }
        >
          <ConditionalRender requiredPermissions="canCreateStockOutflow">
            <SubMenuItem
              to="/autres-sorties"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 13H5V11H19V13Z" />
                  <path d="M13 5V19H11V5H13Z" />
                  <path d="M5 11L12 4L19 11H5Z" />
                </svg>
              }
            >
              Autres Sorties
            </SubMenuItem>
          </ConditionalRender>
          <ConditionalRender requiredPermissions="canCreateStockEntry">
            <SubMenuItem
              to="/autres-entrees"
              icon={
                <svg
                  className="w-4 h-4 rotate-180"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 13H5V11H19V13Z" />
                  <path d="M13 5V19H11V5H13Z" />
                  <path d="M5 11L12 4L19 11H5Z" />
                </svg>
              }
            >
              Autres Entrées
            </SubMenuItem>
          </ConditionalRender>
        </MenuItemWithSubmenu>
      </ConditionalRender>
    </ul>
  );
}
// import { useLocation } from "react-router-dom";
// import image from "../img/logo.png";

export default function DefaultLayout({ children }) {
  return (
    <UserProvider>
      <LayoutContent>{children}</LayoutContent>
    </UserProvider>
  );
}

function LayoutContent({ children }) {
  const { user, error } = useContext(UserContext);
  const [sidebarVisibility, setSidebarVisibility] = useState(false);
  // const location = useLocation();
  const [ShowProfileModal, setShowProfileModal] = useState(false);

  // const displayProfileModal = () => {
  //   setShowProfileModal(!ShowProfileModal);
  // };

  if (!user && !error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          {/* Animation */}
          <div className="loader"></div>
          <div className="w-10 h-10 border-2 border-t-2 border-gray-200 rounded-full animate-spin my-4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>{error}</div>;
  }

  const handleClick = () => {
    setSidebarVisibility(!sidebarVisibility);
  };

  const handleLogoutClick = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("currentUrl");
    window.location.reload();
  };

  return (
    <div className="flex h-dvh overflow-hidden max-w-[2560px] mx-auto">
      <aside
        id="cta-button-sidebar"
        className={[
          "max-lg:fixed relative max-lg:top-0 max-lg:w-2/3 max-lg:left-0 z-50 transition-transform max-lg:shadow-md bg-white h-screen overflow-x-hidden shadow-md",
          sidebarVisibility
            ? "lg:w-0"
            : "max-lg:-translate-x-full lg:w-60 p-3 pt-0 lg:min-w-72",
        ].join(" ")}
      >
        <h1 className="text-3xl mb-8 p-4 font-semibold ">Gest_stock</h1>
        <button
          className="w-10 h-10 absolute z-30 -right-2 top-1/3 grid items-center justify-center rounded-full bg-white shadow-md shadow-[#00000035] border border-gray-400 lg:hidden"
          onClick={handleClick}
        >
          <svg
            className="w-6 h-6"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m17 16-4-4 4-4m-6 8-4-4 4-4"
            />
          </svg>
        </button>
        <SidebarMenu user={user} />
        {/* <div className="bg-amber-50 mt-10 absolute h-24 top-[100%-150px] grid justify-center items-center left-1/2 lg:left-[calc(50%-10px)] -translate-x-1/2 origin-center w-[90%] lg:max-w-52">
          <img
            src={image}
            alt="Logo de duchesse"
            className="h-full object-contain"
          />
        </div> */}
      </aside>
      <div className="flex-grow h-auto overflow-y-auto relative overflow-x-hidden">
        <div
          className="flex items-center justify-between p-3 pr-4 lg:pl-0 pt-6 bg-white sticky top-0 z-50 h-16 shadow-sm"
          id="header"
        >
          <div className="flex gap-4 items-center px-2">
            <button
              className="h-10 w-10 shadow-md border border-gray-200 rounded-md grid justify-center items-center hover:bg-gray-50 transition-colors"
              onClick={handleClick}
            >
              <svg
                className="w-6 h-6 text-gray-600"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M5 7h14M5 12h14M5 17h14"
                />
              </svg>
            </button>
            <div className="flex items-center space-x-3">
              <p className="text-xl font-medium text-gray-800">
                Hello, {user.username}
              </p>
              <RoleBadge role={user?.role} size="sm" />
              {user.role === ROLES.RESP_BOUTIQUE && (
                <RoleBadge country={user?.magasin_name} size="sm" />
              )}
              <CountryBadge country={user?.which_country} size="md" />
            </div>
          </div>
          <ul className="flex items-center gap-4">
            <li className="text-amber-400 h-8 w-8 border-2 border-amber-400 rounded-full grid justify-center items-center hover:bg-amber-50 transition-colors cursor-pointer">
              <svg
                className="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.133 12.632v-1.8a5.406 5.406 0 0 0-4.154-5.262.955.955 0 0 0 .021-.106V3.1a1 1 0 0 0-2 0v2.364a.955.955 0 0 0 .021.106 5.406 5.406 0 0 0-4.154 5.262v1.8C6.867 15.018 5 15.614 5 16.807 5 17.4 5 18 5.538 18h12.924C19 18 19 17.4 19 16.807c0-1.193-1.867-1.789-1.867-4.175ZM8.823 19a3.453 3.453 0 0 0 6.354 0H8.823Z" />
              </svg>
            </li>
            {user.magasin_name && (
              <li className="hidden lg:flex items-center bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-sm font-medium border border-amber-100">
                {user.magasin_name}
              </li>
            )}
            <li className="relative">
              <button
                onClick={() => setShowProfileModal(!ShowProfileModal)}
                id="profile-button"
                className="relative w-8 h-8 overflow-hidden bg-gray-200 rounded-full grid justify-center items-center hover:bg-gray-300 transition-colors border-2 border-gray-300"
              >
                <svg
                  className="w-5 h-5 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>
            </li>
          </ul>

          {/* Profile Modal */}
          {ShowProfileModal && (
            <div className="absolute top-16 right-4 w-72 bg-white shadow-xl rounded-xl overflow-hidden z-50 border border-gray-100">
              {/* En-tête avec dégradé */}
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <h2 className="text-lg font-semibold text-white">
                    Mon Profil
                  </h2>
                </div>
              </div>

              {/* Corps du modal */}
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-700">
                        {user.username}
                      </p>
                      <RoleBadge role={user?.role} size="xs" />
                    </div>
                    <p className="text-xs text-gray-500">Nom d'utilisateur</p>
                  </div>
                </div>

                {user.magasin_name && (
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-amber-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {user.magasin_name}
                      </p>
                      <p className="text-xs text-gray-500">Magasin</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {user.email}
                    </p>
                    <p className="text-xs text-gray-500">Adresse email</p>
                  </div>
                </div>

                <button
                  onClick={handleLogoutClick}
                  className="w-full mt-4 px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-all duration-300 focus:ring-2 focus:ring-red-200"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Se déconnecter
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="h-auto overscroll-y-auto relative">{children}</div>
      </div>
    </div>
  );
}
